import type { DraftState, PlannedOrderSettings, WizardSampleGroup } from '../../Model/OrderWizard/OrderWizardDraft.ts'
import type { NegativeControl, Sample } from '../../Model/Sample/ISample.ts'
import { SampleConfigurationTypes } from '../../Generated/SampleConfigurationType.ts'
import type { DetectedSequencingPlatform } from '../../Util/sequencingPlatformDetection.ts'
import type { RejectionReason } from '../../View/Page/OrderManagementPage/FileHandling/PreValidator.ts'
import { prevalidateFiles } from '../../View/Page/OrderManagementPage/FileHandling/PreValidator.ts'
import { expectsReverseFile } from '../../Util/reverseFilePairing.ts'
import { removalHolding } from './removal.ts'

export type PlanFile = { key: string; name: string; platform: DetectedSequencingPlatform }

export type ItemPlanInput = {
  files: PlanFile[]
  /**
   * Mates as `[forward, reverse]` file keys, from the read-layout detector: confirmed by shared read ids,
   * or paired by one consistent name family (`_R1`/`_R2`, `_1`/`_2`, `fwd`/`rev`, …) with no read against it.
   */
  pairs: [string, string][]
  sampleConfiguration: PlannedOrderSettings['sampleConfiguration']['value']
}

export type ItemPlan = { rejections: Record<string, string>; groups: WizardSampleGroup[] }

type RejectionKey = Exclude<RejectionReason, 'no-read-designation' | 'unpaired'> | 'no-mate' | 'mate-rejected' | 'awaiting-layout' | 'awaiting-mate'

export const REJECTION_TEXT: Record<RejectionKey, string> = {
  'wrong-extension': 'Only compressed .fastq.gz files can be uploaded',
  'platform-mismatch': 'Sequenced on a different platform than the rest of the dropped files',
  'no-mate': 'No mate file found for this paired-end file',
  'mate-rejected': 'Its mate file was rejected',
  'awaiting-layout': 'Waiting for your answer: mate files to come, or merged reads',
  'awaiting-mate': 'Waiting for its mate file: drop it to pair them'
}

export const flagsFor = (sampleConfiguration: ItemPlanInput['sampleConfiguration']) => ({
  isMerged: SampleConfigurationTypes[sampleConfiguration]?.impliesMerged ?? false,
  isTrimmed: SampleConfigurationTypes[sampleConfiguration]?.impliesTrimmed ?? false
})

const waitingGroup = (forward: string, reverse?: string): WizardSampleGroup => ({
  key: forward,
  forward,
  reverse,
  item: { status: 'waiting' }
})

/**
 * Which dropped FASTQ files become which pool items: the upload page's file checks decide which files are
 * acceptable, the read-layout detector's mate pairs which belong together. The upload page's R1/R2 naming
 * rule is not applied, since the pipeline takes both files by id. A paired-end file without a mate is not
 * uploaded.
 */
export const planItems = (input: ItemPlanInput): ItemPlan => {
  const { isMerged } = flagsFor(input.sampleConfiguration)
  /* every file is presented as single-ended, which leaves exactly the naming and partner checks out */
  const described = input.files.map((file) => ({
    file,
    name: file.name,
    meta: { isMerged: true, detectedSequencingPlatform: file.platform }
  }))
  const { accepted, rejected } = prevalidateFiles(described)
  const rejections: Record<string, string> = {}
  for (const { file, reason } of rejected) {
    rejections[file.file.key] =
      reason === 'wrong-extension' || reason === 'platform-mismatch' ? REJECTION_TEXT[reason] : REJECTION_TEXT['no-mate']
  }

  const acceptedFiles = accepted.map((entry) => entry.file)
  const singleEnded = (file: PlanFile) => isMerged || file.platform === 'ONT'
  const groups: WizardSampleGroup[] = acceptedFiles.filter(singleEnded).map((file) => waitingGroup(file.key))

  const needingMates = new Map(acceptedFiles.filter((file) => !singleEnded(file)).map((file) => [file.key, file]))
  for (const [forward, reverse] of input.pairs) {
    const hasForward = needingMates.has(forward)
    const hasReverse = needingMates.has(reverse)
    if (hasForward && hasReverse) {
      groups.push(waitingGroup(forward, reverse))
      needingMates.delete(forward)
      needingMates.delete(reverse)
    } else if (hasForward || hasReverse) {
      const orphan = hasForward ? forward : reverse
      rejections[orphan] = REJECTION_TEXT['mate-rejected']
      needingMates.delete(orphan)
    }
  }
  for (const key of needingMates.keys()) {
    rejections[key] = REJECTION_TEXT['no-mate']
  }
  return { rejections, groups }
}

/** `UNKNOWN` means the reads did not say; the stored column holds a real platform or nothing. */
const storablePlatform = (platform: DetectedSequencingPlatform | undefined) => (platform && platform !== 'UNKNOWN' ? platform : undefined)

/** The pool-content payload for one group once its files are uploaded, in the shape the upload page sends. */
export type ItemPayload = Omit<Sample, 'id'> | Omit<NegativeControl, 'id'>

export const itemPayloadFor = (group: WizardSampleGroup, draft: DraftState): ItemPayload | null => {
  const forward = draft.files[group.forward]
  const reverse = group.reverse ? draft.files[group.reverse] : undefined
  const forwardFileId = forward?.upload?.serverFileId
  const reverseFileId = reverse?.upload?.serverFileId
  if (!draft.settings || !forwardFileId || (group.reverse && !reverseFileId)) {
    return null
  }
  /* the server refuses a lone R1 into a paired order: the item waits for its R2 */
  if (!group.reverse && expectsReverseFile(draft.settings.sampleConfiguration.value) && forward.platform !== 'ONT') {
    return null
  }
  return {
    itemType: group.itemType ?? 'SAMPLE',
    forwardFileId,
    reverseFileId,
    ...flagsFor(draft.settings.sampleConfiguration.value),
    detectedSequencingPlatform: storablePlatform(forward.platform),
    version: null
  }
}

/** Whether a removal not yet done may leave an item holding one of the payload's files. */
export const heldByRemoval = (draft: DraftState, payload: ItemPayload): boolean =>
  !!removalHolding(draft, [payload.forwardFileId, payload.reverseFileId])

/** Groups whose files are all uploaded, held by no item, and that have no item yet, with their payloads. */
export const groupsReadyForItems = (draft: DraftState): { group: WizardSampleGroup; payload: ItemPayload }[] => {
  /* items are made with the format's flags, so not while a format switch is unwritten or refused */
  if (!draft.orderId || draft.poolIds.length === 0 || draft.settingsWrite?.sampleConfiguration) {
    return []
  }
  return Object.values(draft.groups)
    .filter((group) => group.item.status === 'waiting')
    .flatMap((group) => {
      const payload = itemPayloadFor(group, draft)
      return payload && !heldByRemoval(draft, payload) ? [{ group, payload }] : []
    })
}
